import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button, Container, Row, Col, Table, Form } from 'react-bootstrap';
import Swal from "sweetalert2";
import PoServices from '../../../services/PoServices';
import RequestServies from '../../../services/RequestServies';
import MrvEditModal from "../Orders/MrvEditModal"
import FilePreviewModal from "./FilePreviewModal";

const BillingViewModal = ({ show, handleClose, admin, test }) => {
    const [billingDocPreview, setBillingDocPreview] = useState(null);
    const [billingDeptInfo, setBillingDeptInfo] = useState(null);
    const [userRole, setUserRole] = useState("");
    const [isEditingAmount, setIsEditingAmount] = useState(false);
    const [newApprovedAmount, setNewApprovedAmount] = useState("");

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userDetails"));
        setUserRole(user?.data?.user?.role || "");
    }, []);

    const [run, setRun] = useState(0);
    const [poItems, setPoItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uniqueMRVs, setUniqueMRVs] = useState([]);
    const [viewStatus, setViewStatus] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [otherDoc, setOtherDoc] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    const [billRows, setBillRows] = useState([
        { billNo: "", amount: "", taxAmount: "", OtherCharges: "", actualBillAmount: '' }
    ]);
    const [approvedAmount, setApprovedAmount] = useState(0);

    const [billingData, setBillingData] = useState([]);
    const [showMrvEdit, setShowMrvEdit] = useState(false);
    const [selectedMrv, setSelectedMrv] = useState(null);


    const fetchPOItems = useCallback(async (poId) => {
        setLoading(true);
        try {
            const res = await PoServices.AllItemsByPoId(poId);

            if (res?.data?.length > 0) {
                const merged = res.data.map(apiItem => {
                    const apiPartNo = String(apiItem?.inventory?.masterItem?.partNo).trim();
                    const match = admin?.items?.find(ai =>
                        String(ai?.partNo || ai?.inventory?.masterItem?.partNo).trim() === apiPartNo
                    );

                    return {
                        ...apiItem,
                        rate: match?.rate || 0,
                        cgst: match?.cgst || 0,
                        sgst: match?.sgst || 0,
                        igst: match?.igst || 0
                    };
                });

                setPoItems(merged);
            } else {
                setPoItems([]);
            }
        } catch (error) {
            Swal.fire("Error", "Failed to fetch PO items.", "error");
        } finally {
            setLoading(false);
        }
    }, [admin]);   // ✅ dependency here


    useEffect(() => {
        if (poItems.length > 0) {
            const mrvMap = new Map();
            poItems.forEach(item => {
                if (item.mrv && !mrvMap.has(item.mrv._id)) {
                    mrvMap.set(item.mrv._id, item.mrv);
                }
            });
            setUniqueMRVs([...mrvMap.values()]);
        } else {
            setUniqueMRVs([]);
        }
    }, [poItems, run]);

    // 2️⃣ Fetch MRV by billing nos (unchanged)
    const handleFetchbilling = async (billingNos) => {
        try {
            const response = await RequestServies.getMrvByBillingNos(billingNos);
            // response?.data expected to be array of MRVs
            const mrvs = response?.data || [];
            setBillingData(mrvs);
        } catch (error) {
            console.error("Error fetching MRV by billing number:", error);
            setBillingData([]); // fallback
        }
    };

    useEffect(() => {
        if (uniqueMRVs.length > 0) {
            const rows = uniqueMRVs.map(mrv => ({
                billNo: mrv?.billingNo || "",
                amount: mrv?.taxableAmount || 0,
                taxAmount: mrv?.totalTax || 0,
                OtherCharges: mrv?.otherCharges || 0,
                actualBillAmount: mrv?.totalAmount || 0,
                mrvId: mrv?._id,
                mrvNo: mrv?.mrvNo
            }));
            setBillRows(rows);

            // Extract billing numbers and call the API
            const billingNos = uniqueMRVs.map(mrv => mrv?.billingNo).filter(Boolean);
            if (billingNos.length > 0) {
                handleFetchbilling(billingNos);
            }
        } else {
            setBillRows([{ billNo: "", amount: "", taxAmount: "", OtherCharges: "", actualBillAmount: '' }]);
            setBillingData([]);
        }
    }, [uniqueMRVs, run]);

    // Use admin.billing directly for superAdmin (no more BillingDeptServices.getBillingDetailsByPoId)
    useEffect(() => {
        if (show && userRole === "superAdmin") {
            const billingFromPO = admin?.billing || null;

            if (billingFromPO) {
                // If backend already populated billing (object), use it directly
                setBillingDeptInfo(billingFromPO);
                setApprovedAmount(billingFromPO?.approvedAmount || 0);
            } else {
                // No billing linked to this PO yet
                setBillingDeptInfo(null);
                setApprovedAmount(0);
            }
        }
    }, [show, admin, userRole]);

    const formatDate = (dateStr) =>
        dateStr ? new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";

    const markAsViewed = (mrvId) => {
        setViewStatus((prev) => ({ ...prev, [mrvId]: true }));
    };

    const handleViewMRV = (mrv) => {
        if (mrv?.image) {
            const fileUrl = `https://indigoinfra.in/img/mrv/${mrv.image}`;
            setPreviewFile({ url: fileUrl, type: mrv.image.endsWith(".pdf") ? "pdf" : "image" });
            setShowPreview(true);
            markAsViewed(mrv._id);
        } else {
            Swal.fire("Error", "MRV document not available", "error");
        }
    };

    const handleDownloadMRV = async (mrv) => {
        if (mrv?.image) {
            try {
                const imageUrl = `https://indigoinfra.in/img/mrv/${mrv.image}`;
                const response = await fetch(imageUrl);
                if (!response.ok) throw new Error("Failed to fetch image");
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = mrv.image;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                markAsViewed(mrv._id);
            } catch (error) {
                console.error(error);
                Swal.fire("Error", "Failed to download MRV image", "error");
            }
        } else {
            Swal.fire("Error", "MRV image not available", "error");
        }
    };

    const handleBillChange = (index, field, value) => {
        const updatedRows = [...billRows];
        updatedRows[index][field] = value;
        setBillRows(updatedRows);
    };

    const handleModalClose = () => {
        setPoItems([]);
        setUniqueMRVs([]);
        setViewStatus({});
        setBillRows([{ billNo: "", amount: "", taxAmount: "", OtherCharges: "", actualBillAmount: '' }]);
        setApprovedAmount(0);
        setOtherDoc(null);
        setBillingDeptInfo(null);

        handleClose();
    };


    const handleSubmitBilling = async () => {
        if (submitting) return;
        setSubmitting(true);

        if (!admin?._id) {
            Swal.fire("Error", "PO not selected", "error");
            setSubmitting(false);
            return;
        }

        if (userRole === "billing") {
            for (let i = 0; i < uniqueMRVs.length; i++) {
                const mrv = uniqueMRVs[i];
                if (!viewStatus[mrv._id]) {
                    Swal.fire("Error", `Please view MRV No ${mrv.mrvNo}'s attached bill before submitting`, "error");
                    setSubmitting(false);
                    return;
                }
            }
        }



        try {
            const mrvIds = billRows.map(row => row.mrvId);
            const totalOtherCharges = billRows.reduce((sum, row) => sum + Number(row.OtherCharges || 0), 0);
            const totalBillAmount = billRows.reduce((sum, row) => sum + Number(row.actualBillAmount || 0), 0);

            const formData = new FormData();
            formData.append("po", admin._id);
            mrvIds.forEach(id => formData.append("mrvIds[]", id));

            formData.append("totalOtherCharges", totalOtherCharges);
            formData.append("totalBillAmount", totalBillAmount);
            formData.append("approvedAmount", Number(approvedAmount) || 0);
            //formData.append("remarks", remark || "");
            formData.append("status", "submitted");
            if (otherDoc) {
                formData.append("image", otherDoc);
            }

            const res = await (await import('../../../services/BillingDeptService')).default.createNewBillingRecord(formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (res?.data) {
                Swal.fire("Success", "Billing record created successfully", "success");

                // Reset fields after successful submission
                setOtherDoc(null);
                setApprovedAmount(0);
                handleModalClose();
            }
        } catch (error) {
            console.error("Error submitting billing record:", error);
            Swal.fire("Error", "Failed to create billing record", "error");
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditMrv = (mrv) => {
        setSelectedMrv(mrv);
        setShowMrvEdit(true);
    };

    const handleApproveBilling = async () => {
        try {
            setSubmitting(true);

            const finalAmount = isEditingAmount
                ? Number(newApprovedAmount)
                : Number(billingDeptInfo?.approvedAmount || 0);

            const payload = {
                poId: admin._id,
                action: "approve",
                amount: finalAmount
            };

            await PoServices.superAdminBillingAction(payload);

            Swal.fire("Success", "Billing approved successfully", "success");

            // ✅ ONLY HERE parent refresh happens
            if (typeof test === "function") {
                test(prev => prev + 1);
            }

            handleClose();

        } catch (error) {
            Swal.fire("Error", "Unable to approve billing", "error");
        } finally {
            setSubmitting(false);
        }
    };



    const handleReturnToBilling = async () => {
        try {
            setSubmitting(true);

            const { value: remark, isDismissed } = await Swal.fire({
                title: "Enter Reason",
                input: "text",
                inputLabel: "Reason",
                inputPlaceholder: "Enter reason...",
                showCancelButton: true,
                inputValidator: (v) => !v.trim() && "Reason is required"
            });

            if (isDismissed) {
                setSubmitting(false);
                return;
            }

            const payload = {
                poId: admin._id,
                action: "return",
                remark
            };

            await PoServices.superAdminBillingAction(payload);

            Swal.fire("Returned", "Sent back to Billing Team", "info");

            handleModalClose();
            test((prev) => prev + 1);
        } catch (error) {
            Swal.fire("Error", "Unable to return billing", "error");
        } finally {
            setSubmitting(false);
        }
    };

    const handlePreviewBillingDoc = () => {
        if (!billingDeptInfo?.otherDoc) return;

        const fileUrl = `https://indigoinfra.in/img/billing/${billingDeptInfo.otherDoc}`;

        const isPDF = billingDeptInfo.otherDoc.toLowerCase().endsWith(".pdf");

        setBillingDocPreview({
            url: fileUrl,
            type: isPDF ? "pdf" : "image"
        });
    };

    useEffect(() => {
        if (admin?._id && show) {
            fetchPOItems(admin._id);
        }
    }, [admin, show, run, fetchPOItems]);

    return (
        <>
            <FilePreviewModal
                show={showPreview}
                handleClose={() => setShowPreview(false)}
                file={previewFile}
            />
            <MrvEditModal
                show={showMrvEdit}
                handleClose={() => setShowMrvEdit(false)}
                admin={selectedMrv}
                run={setRun}
            />

            <Modal show={show} onHide={handleClose} size="xl" className="fade" enforceFocus={false}>
                <Modal.Header>
                    <Modal.Title>
                        <i className="fas fa-edit"></i> Billing Summary
                    </Modal.Title>
                    <Button variant="" className="btn-close" onClick={handleModalClose}></Button>
                </Modal.Header>

                <Modal.Body>
                    {/* Items & Rates Summary */}
                    <Container className='mt-1 mb-2'>
                        <Row>
                            <Col md="12">
                                <h4 className='text-center'>Items & Rates Summary</h4>
                                {loading ? (
                                    <div className="text-center my-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2 text-muted">Loading PO items...</p>
                                    </div>
                                ) : poItems.length > 0 ? (
                                    <Table striped bordered responsive>
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Part No</th>
                                                <th>Description</th>
                                                <th>PO Qty</th>
                                                <th>Received Qty</th>
                                                <th>Unit</th>
                                                <th>Rate</th>
                                                <th className='fw-bold'>Amount</th>
                                                <th>CGST</th>
                                                <th>SGST</th>
                                                <th>IGST</th>
                                                <th className='fw-bold'>GST Amount</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {poItems.map((item, idx) => {
                                                const amount = (item?.rate || 0) * (item?.qtyRecived || 0);
                                                const gstAmount =
                                                    (amount * (item?.cgst || 0)) / 100 +
                                                    (amount * (item?.sgst || 0)) / 100 +
                                                    (amount * (item?.igst || 0)) / 100;

                                                return (
                                                    <tr key={item._id}
                                                        className={item?.status === "supplier_failed" ? "table-danger" : ""}
                                                    >
                                                        <td>{idx + 1}</td>
                                                        <td>{item?.inventory?.masterItem?.partNo || "N/A"}</td>
                                                        <td>{item?.inventory?.masterItem?.description || "N/A"}</td>
                                                        <td>{item?.approveQty || 0}</td>
                                                        <td>{item?.qtyRecived || 0}</td>
                                                        <td>{item?.inventory?.masterItem?.unit?.name}</td>
                                                        <td>{item?.rate || 0}</td>
                                                        <td className="fw-bold">{amount.toFixed(2)}</td>
                                                        <td>{item?.cgst || 0}</td>
                                                        <td>{item?.sgst || 0}</td>
                                                        <td>{item?.igst || 0}</td>
                                                        <td className="fw-bold">{gstAmount.toFixed(2)}</td>
                                                        <td>{item?.status === "approved" ? "Order Pending" : item?.status}</td>
                                                    </tr>
                                                );
                                            })}

                                            {/* Total Row */}
                                            <tr className="fw-bold bg-light">
                                                <td colSpan="3" style={{ textAlign: "right" }}>Total</td>
                                                <td>
                                                    {poItems.reduce((sum, item) => sum + (item?.approveQty || 0), 0)}
                                                </td>
                                                <td>
                                                    {poItems.reduce((sum, item) => sum + (item?.qtyRecived || 0), 0)}
                                                </td>
                                                <td></td>
                                                <td></td>
                                                <td>
                                                    {poItems
                                                        .reduce(
                                                            (sum, item) => sum + (item?.rate || 0) * (item?.qtyRecived || 0),
                                                            0
                                                        )
                                                        .toFixed(2)}
                                                </td>
                                                <td></td>
                                                <td></td>
                                                <td></td>
                                                <td>
                                                    {poItems
                                                        .reduce((sum, item) => {
                                                            const amount = (item?.rate || 0) * (item?.qtyRecived || 0);
                                                            const gst =
                                                                (amount * (item?.cgst || 0)) / 100 +
                                                                (amount * (item?.sgst || 0)) / 100 +
                                                                (amount * (item?.igst || 0)) / 100;
                                                            return sum + gst;
                                                        }, 0)
                                                        .toFixed(2)}
                                                </td>
                                                <td></td>
                                            </tr>

                                            {/* Grand Total Row */}
                                            <tr className="fw-bold">
                                                <td colSpan="7" style={{ textAlign: "right" }}>
                                                    Grand Total (Amount + GST)
                                                </td>
                                                <td colSpan="5">
                                                    {poItems
                                                        .reduce((sum, item) => {
                                                            const amount = (item?.rate || 0) * (item?.qtyRecived || 0);
                                                            const gst =
                                                                (amount * (item?.cgst || 0)) / 100 +
                                                                (amount * (item?.sgst || 0)) / 100 +
                                                                (amount * (item?.igst || 0)) / 100;
                                                            return sum + amount + gst;
                                                        }, 0)
                                                        .toFixed(2)}
                                                </td>
                                                <td></td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                ) : (
                                    <p className="text-center text-muted">No PO items available.</p>
                                )}
                            </Col>
                        </Row>
                    </Container>

                    {/* PO Summary */}
                    <Container>
                        <Row>
                            <Col md='12'>
                                <h4 className='text-center mt-2'>PO Summary</h4>
                                <Table striped bordered responsive>
                                    <thead>
                                        <tr>
                                            <th>PO No.</th>
                                            <th>Requisition No.</th>
                                            <th>CGST(Rs)</th>
                                            <th>SGST(Rs)</th>
                                            <th>IGST(Rs)</th>
                                            <th>Grand Total(Rs)</th>
                                            <th>Items</th>
                                            <th>Created By</th>
                                            <th>CreatedOn</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>{admin?.poNumber}</td>
                                            <td>{admin?.requisitionNo?.requisitionNo}</td>
                                            <td>{admin?.totalCGSTAmount}</td>
                                            <td>{admin?.totalSGSTAmount}</td>
                                            <td>{admin?.totalIGSTAmount}</td>
                                            <td>{admin?.totalAmount}</td>
                                            <td>{admin?.totalItems}</td>
                                            <td>{admin?.createdBy?.name}</td>
                                            <td>{formatDate(admin?.createdAt)}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>
                    </Container>

                    {/* MRV Summary */}
                    <Container>
                        <Row>
                            <Col md='12'>
                                <h5 className='text-center mt-3 mb-2'>MRV Summary</h5>
                                {uniqueMRVs.length > 0 ? (
                                    <Table striped bordered responsive>
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>MRV No</th>
                                                <th>Store</th>
                                                <th>Supplier</th>
                                                <th>Bill Amount</th>
                                                <th>Other Charges</th>
                                                <th colSpan="2">Bill No.</th>
                                                <th>Bill View/Downlaod</th>
                                                <th>Bill Date</th>
                                                <th>Item Count</th>
                                                <th>Created By</th>
                                                {userRole === "billing" && <th>View Status</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {uniqueMRVs.map((mrv, idx) => (
                                                <tr key={mrv._id}>
                                                    <td>{idx + 1}</td>
                                                    <td>{mrv?.mrvNo}</td>
                                                    <td>{mrv?.store?.name || 'N/A'}</td>
                                                    <td>{mrv?.supplier?.name || 'N/A'}</td>
                                                    <td>{mrv?.totalAmount || 0}</td>
                                                    <td>{mrv?.otherCharges || 0}</td>
                                                    <td colSpan='2'>{mrv?.billingTitle || ''} {mrv?.billingNo || ''}</td>
                                                    <td>
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={() => handleViewMRV(mrv)}
                                                            >
                                                                <i className="fas fa-eye"></i>
                                                            </Button>
                                                            <Button
                                                                variant="light"
                                                                size="sm"
                                                                className="bg-dark border-0 text-white"
                                                                onClick={() => handleDownloadMRV(mrv)}
                                                            >
                                                                <i className="fas fa-file-image text-white"></i> JPG
                                                            </Button>
                                                            {userRole === "billing" && (
                                                                <Button
                                                                    variant="warning"
                                                                    size="sm"
                                                                    onClick={() => handleEditMrv(mrv)}
                                                                >
                                                                    <i className="fas fa-edit"></i>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {mrv?.billingDate
                                                            ? new Date(mrv.billingDate).toLocaleDateString("en-GB", {
                                                                day: "2-digit",
                                                                month: "2-digit",
                                                                year: "numeric",
                                                            })
                                                            : "-"}
                                                    </td>
                                                    <td>{mrv?.itemCount || 0}</td>
                                                    <td>{mrv?.user?.name}</td>
                                                    <td className="text-center">
                                                        {userRole === "billing" && (
                                                            <input type="checkbox" checked={!!viewStatus[mrv._id]} readOnly />
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <p className="text-center text-muted">No MRV data available.</p>
                                )}
                            </Col>
                        </Row>
                    </Container>

                    {/* Billing Data for billing role */}
                    {userRole === "billing" && (
                        <Container className="mt-4">
                            <Row>
                                <Col md="12">
                                    <h5 className="text-center mb-2">
                                        Bill Used Details ({billingData.length})
                                    </h5>
                                    {billingData.length > 0 ? (
                                        <Table striped bordered responsive>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Bill Title</th>
                                                    <th>Bill No.</th>
                                                    <th>MRV No.</th>
                                                    <th>Requisition No</th>
                                                    <th>Item Count</th>
                                                    <th>Other Charges</th>
                                                    <th>Total Amount</th>
                                                    <th>Store</th>
                                                    <th>Supplier</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {billingData.map((bill, idx) => (
                                                    <tr key={bill._id || idx}>
                                                        <td>{idx + 1}</td>
                                                        <td>{bill.billingTitle || "-"}</td>
                                                        <td>{bill.billingNo || "-"}</td>
                                                        <td>{bill.mrvNo || "-"}</td>
                                                        <td>
                                                            {bill.requisitionNumbers && bill.requisitionNumbers.length > 0
                                                                ? bill.requisitionNumbers.join(", ")
                                                                : "-"}
                                                        </td>
                                                        <td>{bill.itemCount || 0}</td>
                                                        <td>{bill.otherCharges || 0}</td>
                                                        <td>{bill.totalAmount || 0}</td>
                                                        <td>{bill.store?.name || "-"}</td>
                                                        <td>{bill.supplier?.name || "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <p className="text-center text-muted">No billing data fetched.</p>
                                    )}
                                </Col>
                            </Row>
                        </Container>
                    )}

                    {/* Attached Bill Details for billing role */}
                    {userRole === "billing" && (
                        <Container>
                            <Row>
                                <Col md="12">
                                    <h5 className="text-center mt-4 mb-2">Attached Bill Details</h5>
                                    <Table bordered striped responsive>
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Bill No</th>
                                                <th>Other Charges</th>
                                                <th>Bill Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {billRows.map((row, index) => (
                                                <tr key={index}>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        <Form.Control
                                                            type="text"
                                                            value={row.billNo}
                                                            readOnly
                                                            onChange={(e) =>
                                                                handleBillChange(index, "billNo", e.target.value)
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            value={row.OtherCharges}
                                                            readOnly
                                                            onChange={(e) =>
                                                                handleBillChange(index, "OtherCharges", e.target.value)
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            readOnly
                                                            value={row.actualBillAmount}
                                                            onChange={(e) => handleBillChange(index, "actualBillAmount", e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>

                                    {/* Totals Section */}
                                    <div className="d-flex justify-content-end mt-3">
                                        <Row style={{ width: "100%" }}>
                                            <Col md={6} className="d-flex justify-content-end">
                                                <Table bordered style={{ maxWidth: "500px" }}>
                                                    <tbody>
                                                        <tr>
                                                            <td className="fw-bold">Total Other Charges</td>
                                                            <td>
                                                                {billRows
                                                                    .reduce((sum, row) => sum + Number(row.OtherCharges || 0), 0)
                                                                    .toFixed(2)}
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td className="fw-bold text-success">Total Bill Amount</td>
                                                            <td className="fw-bold text-success">
                                                                {billRows
                                                                    .reduce((sum, row) => sum + Number(row.actualBillAmount || 0), 0)
                                                                    .toFixed(2)}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td className="fw-bold text-primary">Final(Bill Amount-Other Charges)</td>
                                                            <td className="fw-bold text-primary">
                                                                {billRows
                                                                    .reduce(
                                                                        (sum, row) =>
                                                                            sum + (Number(row.actualBillAmount || 0) - Number(row.OtherCharges || 0)),
                                                                        0
                                                                    )
                                                                    .toFixed(2)}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </Col>

                                            <Col md={6} className="d-flex align-items-start justify-content-center">
                                                {otherDoc && (
                                                    <div
                                                        className="border p-2 rounded"
                                                        style={{ maxHeight: "300px", overflow: "auto", width: "100%" }}
                                                    >
                                                        <p className="fw-bold text-center mb-2">Others Documents Preview</p>

                                                        {otherDoc.type === "application/pdf" ? (
                                                            <embed
                                                                src={URL.createObjectURL(otherDoc)}
                                                                type="application/pdf"
                                                                width="100%"
                                                                height="300px"
                                                                style={{ borderRadius: "5px" }}
                                                            />
                                                        ) : (
                                                            <img
                                                                src={URL.createObjectURL(otherDoc)}
                                                                alt="Preview"
                                                                style={{
                                                                    width: "100%",
                                                                    height: "auto",
                                                                    borderRadius: "5px",
                                                                    display: "block",
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>
                                    </div>
                                </Col>
                            </Row>
                        </Container>
                    )}

                    {/* Approved Amount + Other Documents for billing role */}
                    {userRole === "billing" && (
                        <Container className="mt-3">
                            <Row className="justify-content-end">
                                <Col md={6}>
                                    <Form.Group controlId="approvedAmount">
                                        <Form.Label className="fw-bold">Approved Amount</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={approvedAmount}
                                            onChange={(e) => setApprovedAmount(e.target.value)}
                                            placeholder="Enter Approved Amount"
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group controlId="otherDocuments">
                                        <Form.Label className="fw-bold">Other Documents</Form.Label>
                                        <Form.Control
                                            type="file"
                                            multiple
                                            onChange={(e) => setOtherDoc(e.target.files[0])}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Container>
                    )}

                    {/* superAdmin approval summary */}
                    {userRole === "superAdmin" && billingDeptInfo && (
                        <Container className="mt-4">
                            <h5 className="text-center fw-bold mb-3">Billing Approval Summary</h5>

                            <Table bordered striped>
                                <tbody>
                                    <tr>
                                        <td className="fw-bold">Approved Amount</td>
                                        <td>
                                            {!isEditingAmount ? (
                                                <>
                                                    {billingDeptInfo.approvedAmount || 0}
                                                    <Button
                                                        variant="link"
                                                        className="ms-2"
                                                        onClick={() => {
                                                            setNewApprovedAmount(billingDeptInfo.approvedAmount || 0);
                                                            setIsEditingAmount(true);
                                                        }}
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </Button>
                                                </>
                                            ) : (
                                                <Form.Control
                                                    type="number"
                                                    value={newApprovedAmount}
                                                    onChange={(e) => setNewApprovedAmount(e.target.value)}
                                                    className="w-50 d-inline-block"
                                                />
                                            )}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td className="fw-bold">Remark</td>
                                        <td>{billingDeptInfo.remarks || "No remark"}</td>
                                    </tr>

                                    <tr>
                                        <td className="fw-bold">Uploaded Document</td>
                                        <td>
                                            {billingDeptInfo.otherDoc ? (
                                                <Button
                                                    variant="link"
                                                    onClick={handlePreviewBillingDoc}
                                                    style={{ padding: 0, fontWeight: "bold" }}
                                                >
                                                    📄 View Document
                                                </Button>
                                            ) : (
                                                "No Document"
                                            )}
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>

                            {/* Preview window under table */}
                            {billingDocPreview && (
                                <div className="mt-3 p-2 border rounded">
                                    <h6 className="text-center fw-bold mb-2">Billing Document Preview</h6>

                                    {billingDocPreview.type === "pdf" ? (
                                        <embed
                                            src={billingDocPreview.url}
                                            type="application/pdf"
                                            width="100%"
                                            height="400px"
                                        />
                                    ) : (
                                        <img
                                            src={billingDocPreview.url}
                                            alt="Billing Document"
                                            style={{
                                                width: "100%",
                                                maxHeight: "400px",
                                                objectFit: "contain",
                                                borderRadius: "5px",
                                            }}
                                        />
                                    )}
                                </div>
                            )}
                        </Container>
                    )}

                </Modal.Body>

                <Modal.Footer className="d-flex justify-content-center">
                    <div className="d-flex gap-2">
                        {userRole === "superAdmin" ? (
                            <>
                                <Button
                                    variant="success"
                                    onClick={handleApproveBilling}
                                    disabled={submitting}
                                >
                                    <i className="fa-solid fa-check text-white fs-5 mb-1 me-1"></i>
                                    {submitting ? "Processing..." : "Approve"}
                                </Button>

                                <Button
                                    variant="warning"
                                    onClick={handleReturnToBilling}
                                    disabled={submitting}
                                >
                                    <i className="fa-solid fa-rotate-left text-white fs-5 mb-1 me-1"></i>
                                    {submitting ? "Processing..." : "Return to Billing Team"}
                                </Button>
                            </>
                        ) : (
                            <Button
                                variant="primary"
                                onClick={handleSubmitBilling}
                                disabled={submitting}
                            >
                                {submitting ? "Submitting..." : "Submit"}
                            </Button>
                        )}

                        <Button variant="danger" onClick={handleModalClose}>
                            Close
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BillingViewModal;
